import { type ReactNode, type Ref } from "react";

import { FormFieldAssistiveText } from "#components/shared/FormFieldAssistiveText";
import { FormFieldHeader } from "#components/shared/FormFieldHeader";
import { cn, customTv } from "#utils";
import {
  Input as ReactAriaInput,
  type InputProps as ReactAriaInputProps,
  type TextFieldProps as ReactAriaTextFieldProps,
  TextField as ReactAriaTextField,
} from "react-aria-components";

const inputStyles = customTv({
  base: `
    text-label-medium text-text-primary py-xs outline-none border border-outline-input-enabled bg-primary rounded-1 px-lg leading-md-20 truncate
    hover:border-outline-input-hover hover:ring-1 hover:ring-outline-input-hover
    focus:border-outline-input-focus focus:ring-1 focus:ring-outline-input-focus
    disabled:border-outline-input-disabled disabled:cursor-not-allowed disabled:text-text-disabled disabled:bg-background-primary disabled:hover:ring-0 disabled:hover:border-outline-input-disabled
    placeholder:text-text-secondary disabled:placeholder:text-text-disabled
  `,
  variants: {
    variant: {
      primary: "",
    },
    isReadOnly: {
      true: "!cursor-text !bg-background-secondary !text-text-primary !border-transparent hover:ring-0 focus:ring-0",
    },
    isInvalid: {
      true: "border-outline-negative ring-1 ring-outline-negative",
    },
  },
});

interface InputProps extends ReactAriaInputProps {
  variant?: "primary";
  className?: string;
}

export interface TextFieldProps extends ReactAriaTextFieldProps {
  inputProps?: InputProps;
  label?: string;
  assistiveText?: ReactNode;
  tooltipText?: ReactNode;
  className?: string;
  ref?: Ref<HTMLInputElement>;
}

export function TextField({
  inputProps,
  label,
  tooltipText,
  className,
  assistiveText,
  isDisabled,
  isReadOnly,
  isInvalid,
  isRequired,
  ref,
  ...rest
}: TextFieldProps) {
  return (
    <ReactAriaTextField
      className={cn("flex flex-col gap-[.15rem]", className)}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
      {...rest}
    >
      <FormFieldHeader
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        tooltipText={tooltipText}
      />
      <ReactAriaInput
        {...inputProps}
        className={inputStyles({
          variant: inputProps?.variant,
          className: inputProps?.className,
          isReadOnly,
          isInvalid,
        })}
        placeholder={inputProps?.placeholder ?? "Type here..."}
        ref={ref}
      />
      <FormFieldAssistiveText isDisabled={isDisabled} isInvalid={isInvalid}>
        {assistiveText}
      </FormFieldAssistiveText>
    </ReactAriaTextField>
  );
}
